// 3rd party
import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { Wizard } from 'react-use-wizard';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Types
import { User, WizardFooterProps } from 'types';
import {
  BillTo,
  ItemOrder,
  OrderAddress,
  OrderInput,
  OrderInputWithSFFlag,
  OrderResponse
} from 'types/parts';
import { CartOrderDetails, CartPurchasable, CartState } from 'types/parts/cart';

// Constants
import { defaultOrderDetails } from 'constants/forms';
import { sfEnabled } from 'constants/featureFlags';
import { UNIVERSAL_PRODUCTS_KEY } from 'constants/cart';

// Providers
import { useLanguage } from 'providers';

// Helpers
import {
  hasRequiredAddressDetails,
  hasRequiredBilleeDetails,
  isValidEmail
} from 'common/helpers/v1';

// Components
import { default as CartHeader } from './CartHeader';
import { default as CartFooter } from './CartFooter';
import { default as CartProductTable } from './CartStepOne';
import { default as CartStepTwo } from './CartStepTwo';
import { default as CartStepThree } from './CartStepThree';

// API
import { iopsApi } from 'api';

// State selectors
import { useCart, useUser } from 'selectors';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import { ToastMsg } from 'common/components/Toast';
import { CartWizardContainer, CartWrapper } from './CartWizard.elements';
import { format, utcToZonedTime } from 'date-fns-tz';
import CartSelectionStep from './CartSelectionStep';
import {
  CartPriceType,
  checkCartItemsPrice,
  checkCartPriceTypeBySelection,
  getSelectedCartState,
  OrderBtnText
} from './helper';
import CartSuccessPage from './CartSuccessPage';
import { OrderInputWithPriceInfo } from 'types/parts';
import { cloneDeep } from 'lodash';
import { cartActions } from 'actions';

export const ENABLE_TWO_WAYS_SYNC = import.meta.env.VITE_TWO_WAY_CART_SYNC_FEATURE === 'true';

// Header content
const stepHeaders = [
  {
    header: 'step_verify_cart',
    description: 'step_verify_description'
  },
  {
    header: 'step_shipping_billing',
    description: 'step_shipping_billing_description'
  },
  {
    header: 'make_a_selection',
    description: 'selection_description',
    twoWaySyncFeature: true // this flag use to check that twoWaySync feature should be enabled to show this.
  },
  {
    header: 'final_estimate',
    description: 'To continue purchasing the product confirm the final estimation.'
  }
];

// Props
interface Props {
  active?: boolean;
  handleClose?: () => void;
}

const getOrderInput = (
  orderDetails: CartOrderDetails,
  cart: CartState,
  user: User,
  sfEnabled: boolean,
  lang: string,
  region: string,
  isProsealBU = false
): OrderInput => {
  const billTo = { ...orderDetails.billee, ...orderDetails.billingAddress };
  const purchasables: ItemOrder[] = [];
  const nonPurchasables: ItemOrder[] = [];
  const nonePriceItems =
    cart.nonePriceItems && cart.nonePriceItems.length > 0
      ? cart.nonePriceItems
      : cart.items.filter((item) => item.item.isPriceMissing);
  cart.items.forEach((item) => {
    const updatedItem: ItemOrder = {
      name: item.item.id,
      id: item.item.id,
      quantity: item.quantity,
      urgent: item.urgent,
      procare_allowance: item.procareAllowance,
      currency: item.item.priceUnit,
      machineDescription: item.groupDescription,
      customerNumber: item.item.custNum,
      sku: item.item.sku,
      unitPrice: item.item.custPrice ?? item.item.price,
      siteRef: item.item.siteRef,
      isTwoWayOrder: isProsealBU
    };
    if (item.groupId !== UNIVERSAL_PRODUCTS_KEY) {
      updatedItem.machineId = item.groupId;
    }

    if (!item?.item?.isPriceMissing) purchasables.push(updatedItem);
  });
  nonePriceItems.forEach((item) => {
    const updatedItem: ItemOrder = {
      name: item.item.id,
      id: item.item.id,
      quantity: item.quantity,
      urgent: item.urgent,
      procare_allowance: item.procareAllowance,
      currency: item.item.priceUnit,
      machineDescription: item.groupDescription,
      customerNumber: item.item.custNum,
      sku: item.item.sku,
      unitPrice: item.item.price,
      siteRef: item.item.siteRef,
      isTwoWayOrder: isProsealBU
    };
    if (item.groupId !== UNIVERSAL_PRODUCTS_KEY) {
      updatedItem.machineId = item.groupId;
    }
    nonPurchasables.push({ ...updatedItem, currency: null });
  });
  const order: OrderInputWithSFFlag = {
    creator: orderDetails.creator || user.email,
    approver: orderDetails.approver,
    billTo: billTo as BillTo,
    shipTo:
      !orderDetails.shipMatchBilling && orderDetails.shippingAddress
        ? ({ ...orderDetails.billee, ...orderDetails.shippingAddress } as OrderAddress)
        : (billTo as BillTo),
    orders: purchasables,
    unpurchasableItems: nonPurchasables,
    customerPoNumber: orderDetails.customerPoNumber || '',
    sfAccountId: !isProsealBU ? orderDetails.accountId : '0013g00000LZuj4AAD',
    sfCartId: cart.sfCart.cartSummary?.cartId,
    allowPartialShipments: orderDetails.allowPartialShipments,
    sfEnabled: sfEnabled,
    lang: lang,
    region: region,
    files: orderDetails.files,
    requested_date: orderDetails.requestedDate,
    ...(orderDetails.customerNumber && { customerNumber: orderDetails.customerNumber }),
    notes: orderDetails.notes,
    isTwoWayOrder: isProsealBU
  };
  return order;
};

const CartWizard = ({ active, handleClose }: Props): ReactElement => {
  const cart = useCart() as CartState;
  const dispatch = useDispatch();
  // Receiving state at the higher level to manage step progression
  const [orderRequest, updateOrderRequest] = useState<OrderResponse>();
  const [orderUrl, setOrderUrl] = useState<{ pricedUrl?: string; nonePricedUrl?: string }>();
  const [downloadDraftPDFLoader, setDownloadDraftPDFLoader] = useState(false);
  const [orderInputData, setOrderInputData] = useState<OrderInputWithSFFlag>();
  const [orderPriceInfo, setOrderPriceInfo] = useState<{
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  }>();
  const [selectedCartPriceType, setSelectedCartPriceType] = useState<CartPriceType>();
  const [priceCalculationLoading, setPriceCalculationStatus] = useState<boolean>(false);
  const [cartSelectionState] = useState<string>('');
  const [erpOrderNumber, setErpOrderNumber] = useState<{
    pricedNumber?: string;
    nonePricedNumber?: string;
  }>({});

  const isPurchageOrderRequired = useMemo(
    () =>
      cart.items.some((product) => {
        product.item.purchaseOrderRequired;
      }),
    [cart]
  );
  const [orderDetails, updateOrderDetails] = useState<CartOrderDetails>({
    ...defaultOrderDetails,
    purchaseOrderRequired: isPurchageOrderRequired
  });

  const { t } = useTranslation(['fpns']);
  const { languageId, region } = useLanguage();
  const [isCartsEmpty, setIsCartsEmpty] = useState<boolean | undefined>();
  const [isItemsLeftIncart, setIsItemsLeftInCart] = useState(false);

  const user = useUser() as User;
  useEffect(() => {
    const isPurchageOrderRequired = cart.items.some(
      (product) => product.item.purchaseOrderRequired
    );
    updateOrderDetails({ ...orderDetails, purchaseOrderRequired: isPurchageOrderRequired });
  }, [cart]);
  const [createOrder, { isLoading: isOrderLoading }] = iopsApi.useCreateOrderMutation();
  const [confirmOrder, { isLoading: isConfirmOrderLoading }] = iopsApi.useConfirmOrderMutation();
  const [draftOrderQuote] = iopsApi.useDraftOrderQuoteMutation();
  const [deleteRemoteCartItems] = iopsApi.useDeleteRemoteCartItemsMutation();

  const handleUpdate = (updatedDetails: CartOrderDetails) => {
    updateOrderDetails({ ...updatedDetails });
  };
  // check to see if the cart has selected items or not
  const hasSelectedItems = useMemo(() => cart.items.some((product) => product.selected), [cart]);

  const cartPriceType = useMemo(() => checkCartItemsPrice(cart), [cart]);

  const cartPriceTypeBySelection = useMemo(
    () => checkCartPriceTypeBySelection(cart),
    [cart, hasSelectedItems]
  );

  const isProsealBU = useMemo(
    () => cart.items.some((product) => product.item.businessUnit === 'proseal'),
    [cart]
  );
  useEffect(() => {
    if (cartPriceTypeBySelection) {
      setSelectedCartPriceType(cartPriceTypeBySelection);
    }
  }, [cartPriceTypeBySelection, cartSelectionState]);

  const handlePriceTypeSelection = (priceType: CartPriceType) => {
    setSelectedCartPriceType(priceType);
  };

  // to enable this feature only remove first condition -> import.meta.env.VITE_TWO_WAY_CART_SYNC_FEATURE === "true".
  const PROSEAL_TWO_WAY_CART_SYNC_FEATURE: boolean = useMemo(() => {
    return (
      ENABLE_TWO_WAYS_SYNC &&
      // cartPriceType === 'mix' &&
      isProsealBU
    );
  }, []);

  const checkCartItemsType = useMemo(() => checkCartItemsPrice(cart), [cart]);

const removeOrderedItem = (order: OrderInputWithSFFlag, products: CartPurchasable[]): void => {
  order.orders.forEach((orderItem) => {
    const matchedProduct = products.find((product) => product.item.id === orderItem.id);
    if (matchedProduct) {
       deleteRemoteCartItems(matchedProduct.item.id).unwrap().then(() => {
                 dispatch({
                  type: cartActions.REMOVE_FROM_CART,
                  item: matchedProduct.item,
                  quantity: matchedProduct.quantity,
                  groupId: matchedProduct.groupId
              });
      });
    }
  });

  order.unpurchasableItems?.forEach((nonPurchasableItem) => {
    const matchedProduct = products.find((product) => product.item.id === nonPurchasableItem.id);
    if (matchedProduct) {
         deleteRemoteCartItems(matchedProduct.item.id).unwrap().then(() => {
                 dispatch({
                  type: cartActions.REMOVE_FROM_CART,
                  item: matchedProduct.item,
                  quantity: matchedProduct.quantity,
                  groupId: matchedProduct.groupId
              });
      });
    }
  });
  

};
  const processOrderInput = async () => {
    // Create order input payload to generate price and tax calculation.
    if (cart) {
      const cartData = getSelectedCartState(cart, cartPriceType);
      if (cartData) {
        const order: OrderInputWithSFFlag = getOrderInput(
          orderDetails,
          cartData,
          user,
          sfEnabled(),
          languageId,
          region,
          true
        );
        setOrderInputData(order);
      }
    }
  };
  const handleDownloadDraftPO = () => {
    const cartState = getSelectedCartState(cart, selectedCartPriceType);
    setDownloadDraftPDFLoader(true);
    if (cartState) {
      try {
        const order: OrderInputWithSFFlag = getOrderInput(
          orderDetails,
          cartState,
          user,
          sfEnabled(),
          languageId,
          region,
          PROSEAL_TWO_WAY_CART_SYNC_FEATURE
        );
        draftOrderQuote(order)
          .unwrap()
          .then((orderResponse) => {
            setDownloadDraftPDFLoader(false);
            if (orderResponse && orderResponse.url) {
              let fileName = '';
              const url = orderResponse.url;
              const match = url.match(/Draft Quote (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})Z\.pdf/);
              if (match) {
                const utcDateString = match[1] + 'Z'; // "2025-04-15T09:50:50Z"

                // 2. Convert to local timezone
                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const localDate = utcToZonedTime(utcDateString, userTimeZone);

                // 3. Format for display or filename
                const formatted = format(localDate, "MM-dd-yyyy HH'hr'mm'min'");

                fileName = `Draft Quote ${formatted}`;
              }
              saveAs(url, fileName);
            } else {
              console.error('Unable to download order quote pdf');
              ToastMsg({
                heading: 'Error',
                type: 'error',
                message: 'Unable to download order quote pdf!'
              });
            }
          })
          .catch(() => {
            setDownloadDraftPDFLoader(false);
            ToastMsg({
              heading: 'Error',
              type: 'error',
              message: 'Unable to download order quote pdf!'
            });
          });
      } catch (err) {
        setDownloadDraftPDFLoader(false);
        console.error(`Failed to manually validate token. Forcing logout. Error: ${err}`);
        ToastMsg({
          heading: 'Error',
          type: 'error',
          message: 'Unable to download order quote pdf!'
        });
      }
    }
  };

  async function handleOrderRequest(cartData = cart) {
    // quote Request used to order the none-price items
    if (cartData) {
      const order: OrderInputWithSFFlag = PROSEAL_TWO_WAY_CART_SYNC_FEATURE
        ? getOrderInput(
            orderDetails,
            cartData,
            user,
            sfEnabled(),
            languageId,
            region,
            true
           // orderPriceInfo
          )
        : getOrderInput(
            orderDetails,
            cartData,
            user,
            sfEnabled(),
            languageId,
            region,
            false
            //  orderPriceInfo
          );

      if (PROSEAL_TWO_WAY_CART_SYNC_FEATURE) {
        const nonePricedItems = cloneDeep(order.unpurchasableItems);
        if (order.orders && order.orders.length > 0) {
          Object.assign(order, { unpurchasableItems: [], ...orderPriceInfo?.pricedInfo });
          await placeOrderRequest(order, cartData, 'price');
        }
        setTimeout(async () => {
          if (nonePricedItems && nonePricedItems.length > 0) {
            Object.assign(order, {
              orders: [],
              unpurchasableItems: nonePricedItems,
              ...orderPriceInfo?.nonePricedInfo
            });
            await placeOrderRequest(order, cartData, 'none-price');
          }
        }, 300);
      } else {
        await placeOrderRequest(order, cartData);
      }
    }
  }
  const placeOrderRequest = async (
    order: OrderInputWithSFFlag,
    cartData: CartState,
    twoWayOrderType?: string
  ) => {
    await createOrder(order)
      .unwrap()
      .then(async (orderResponse) => {
        const purchasableItems = cartData.items.filter((item) => item.item.isPurchasable);
        const totalValue = purchasableItems.reduce(
          (sum, item) => sum + (item.item.custPrice || 0) * item.quantity,
          0
        );
        const currency = purchasableItems.length > 0 ? purchasableItems[0].item.priceUnit : '';

        if (orderResponse && orderResponse.url) {
          generateAnalyticsEvent({
            category: AnalyticsCategories.ECOMMERCE,
            action: ECommerceAnalyticEventActions.ORDER_QUOTE_GENERATED,
            transaction_id: orderResponse.id,
            event: ECommerceAnalyticEventActions.PURCHASE,
            currency: currency,
            value: totalValue,
            items: purchasableItems.map((item) => ({
              id: item.item.id,
              item_id: item.item.id,
              item_name: item.item.name as string,
              quantity: item.quantity,
              urgent: item.urgent,
              currency: item.item.priceUnit,
              price: item.item.custPrice,
              procare_allowance: item.procareAllowance,
              machineDescription: item.groupDescription
            }))
          });
          generateAnalyticsEvent({
            category: AnalyticsCategories.ECOMMERCE,
            action: ECommerceAnalyticEventActions.PURCHASE,
            transaction_id: orderResponse.id,
            event: ECommerceAnalyticEventActions.PURCHASE,
            currency: currency,
            value: totalValue,
            items: purchasableItems.map((item) => ({
              id: item.item.id,
              item_id: item.item.id,
              item_name: item.item.name as string,
              quantity: item.quantity,
              urgent: item.urgent,
              currency: item.item.priceUnit,
              procare_allowance: item.procareAllowance,
              price: item.item.custPrice,
              machineDescription: item.groupDescription
            }))
          });
          const orderType = checkCartItemsPrice(cartData);
          // Save the response to show PDF later
          updateOrderRequest({
            ...orderResponse,
            requestedQuoteId: orderResponse.orderNumber,
            orderType
          });
          setOrderUrl((prev) => ({
            pricedUrl: twoWayOrderType === 'price' ? orderResponse.url : prev?.pricedUrl,
            nonePricedUrl:
              twoWayOrderType === 'none-price' ? orderResponse.url : prev?.nonePricedUrl
          }));
          await confirmOrder({
            id: orderResponse.id,
            email: orderDetails.creator,
            status: true,
            sfPoId: orderResponse.sfPoId,
            sfEnabled: sfEnabled(),
            sfAccountId: orderDetails.accountId,
            requestedDate: orderDetails.requestedDate, //
            erpOrderNumber:
              twoWayOrderType === 'price'
                ? orderPriceInfo?.pricedInfo?.erpOrderNumber
                : twoWayOrderType === 'none-price'
                ? orderPriceInfo?.nonePricedInfo?.erpOrderNumber
                : undefined, //
            isTwoWayOrder: PROSEAL_TWO_WAY_CART_SYNC_FEATURE
          })
            .unwrap()
            .then((response) => {
              if (response && response.erpOrderNumber) {
                if (twoWayOrderType === 'price') {
                  setErpOrderNumber((prev) => ({
                    ...prev,
                    pricedNumber: response.erpOrderNumber as string
                  }));
                } else if (twoWayOrderType === 'none-price') {
                  setErpOrderNumber((prev) => ({
                    ...prev,
                    nonePricedNumber: response.erpOrderNumber as string
                  }));
                }
              }
            })
            .catch((error) => {
              console.error('error source::: ', error);
              console.warn('Order confirmation error: ', error);
              ToastMsg({
                heading: 'Error',
                message: t('error_order_confirmation'),
                type: 'error'
              });
            });
          removeOrderedItem(order, [...cartData.items, ...(cartData.nonePriceItems || [])]);
        } else {
          console.error('Unable to process order request');
          ToastMsg({
            heading: 'Error',
            message: t('error_order_request'),
            type: 'error'
          });
        }
      })
      .catch((error) => {
        console.error('error source::: ', error);
        console.warn('Order request error: ', error);
        ToastMsg({
          heading: 'Error',
          message: t('error_order_request'),
          type: 'error'
        });
      });
  };
  const stepFooters: WizardFooterProps[] = [
    {
      nextText: 'continue',
      continueCondition: () => {
        return cart && cart.items.length > 0 && !isCartsEmpty;
      }
    },
    {
      nextText: 'continue',
      prevText: 'clear_cart',
      continueCondition: () => {
        return Boolean(
          hasRequiredAddressDetails(orderDetails.billingAddress as OrderAddress) &&
            (orderDetails.shipMatchBilling ||
              (orderDetails.shippingAddress &&
                hasRequiredAddressDetails(orderDetails.shippingAddress as OrderAddress))) &&
            hasRequiredBilleeDetails(orderDetails.billee) &&
            orderDetails.creator &&
            isValidEmail(orderDetails.creator) &&
            (orderDetails.approver === '' || isValidEmail(orderDetails.approver)) &&
            (orderDetails.purchaseOrderRequired ? orderDetails.customerPoNumber : true)
        );
      },
      nextAction: PROSEAL_TWO_WAY_CART_SYNC_FEATURE ? processOrderInput : handleOrderRequest
    },
    ...(PROSEAL_TWO_WAY_CART_SYNC_FEATURE && cartPriceType === 'mix'
      ? [
          {
            nextText: 'continue',
            continueCondition: () =>
              !!selectedCartPriceType && !(isOrderLoading || isConfirmOrderLoading),
            nextAction: async () => {
              if (!orderInputData) {
                processOrderInput();
              }
            }
          }
        ]
      : []),
    {
      nextText: PROSEAL_TWO_WAY_CART_SYNC_FEATURE
        ? OrderBtnText(
            selectedCartPriceType !== 'nill'
              ? (selectedCartPriceType as CartPriceType)
              : checkCartItemsPrice(cart)
          )
        : 'download_pdf',
      prevText: 'back',
      nextAction: async () => {
        if (PROSEAL_TWO_WAY_CART_SYNC_FEATURE) {
          const placePronetOrder = true;
          const cartState = getSelectedCartState(
            cart,
            selectedCartPriceType !== 'nill'
              ? (selectedCartPriceType as CartPriceType)
              : checkCartItemsPrice(cart),
            undefined,
            placePronetOrder
          );
          await handleOrderRequest(cartState);
          // setIsItemsLeftInCart(false);
        } else if (orderRequest?.url) {
          const url = orderRequest.url;
          const pathname = url?.split('?')[0];
          const pathnameParts = pathname?.split('/');
          const originalFilename = pathnameParts[pathnameParts?.length - 1];
          saveAs(url, originalFilename);
        }
      },
      continueCondition: () => !(isOrderLoading || isConfirmOrderLoading)
    }
  ];

  const checkIsUrgent = () => {
    const isUrgent = cart.items.some((cartItem) => cartItem.urgent);
    return isUrgent;
  };

  const stepHeadersItem = useMemo(() => {
    return stepHeaders.filter((step) =>
      step.twoWaySyncFeature ? PROSEAL_TWO_WAY_CART_SYNC_FEATURE && cartPriceType === 'mix' : true
    );
  }, [cartPriceType]);

  return (
    <CartWizardContainer>
      {active &&
        (orderRequest?.id &&
        erpOrderNumber &&
        !isItemsLeftIncart &&
        PROSEAL_TWO_WAY_CART_SYNC_FEATURE ? (
          <CartSuccessPage
            requestType={orderRequest.orderType as CartPriceType}
            handleClose={handleClose}
            orderUrl={orderUrl}
            erpOrderNumber={erpOrderNumber}
          />
        ) : (
          <Wizard
            header={
              <CartHeader
                isCartsEmpty={!cart.items.length}
                stepHeaders={stepHeadersItem}
                onDownloadDraftPO={handleDownloadDraftPO}
                downloadDraftPDFLoader={downloadDraftPDFLoader}
                twoWaySyncFeature={PROSEAL_TWO_WAY_CART_SYNC_FEATURE}
                cartBannerState={{
                  cartPriceType,
                  selectedCartPriceType,
                  pricedQuoteRequestId: orderPriceInfo?.pricedInfo?.erpOrderNumber,
                  nonePricedQuoteRequestId: orderPriceInfo?.nonePricedInfo?.erpOrderNumber
                }}
                cartItemsType={checkCartItemsType}
              />
            }
            footer={
              <CartFooter
                selectedPriceType={selectedCartPriceType}
                isLoading={isOrderLoading || isConfirmOrderLoading}
                handleClose={handleClose}
                stepFooters={stepFooters}
                orderPriceInfo={orderPriceInfo}
                priceCalculationLoading={priceCalculationLoading}
                isTwoWaySyncEnabled={PROSEAL_TWO_WAY_CART_SYNC_FEATURE}
              />
            }
            wrapper={<CartWrapper />}
          >
            <CartProductTable
              cartPriceType={cartPriceType}
              handleClose={handleClose}
              enableProsealTwoWaySync={PROSEAL_TWO_WAY_CART_SYNC_FEATURE}
            />
            <CartStepTwo
              orderDetails={orderDetails}
              isUrgent={checkIsUrgent()}
              handleUpdate={handleUpdate}
            />
            {PROSEAL_TWO_WAY_CART_SYNC_FEATURE && cartPriceType === 'mix' && (
              <CartSelectionStep
                selectedPriceType={selectedCartPriceType}
                cart={cart}
                handlePriceTypeSelection={handlePriceTypeSelection}
              />
            )}
            {PROSEAL_TWO_WAY_CART_SYNC_FEATURE ? (
              <CartProductTable
                cartPriceType={cartPriceType}
                handleClose={handleClose}
                selectedPriceType={selectedCartPriceType}
                isLoading={isOrderLoading || isConfirmOrderLoading}
                isFinalEstimationPage
                requestedDate={orderDetails.requestedDate}
                orderInputData={orderInputData}
                setOrderInputData={setOrderInputData}
                setOrderPriceInfo={setOrderPriceInfo}
                setPriceCalculationStatus={setPriceCalculationStatus}
                enableProsealTwoWaySync={PROSEAL_TWO_WAY_CART_SYNC_FEATURE}
              />
            ) : (
              <CartStepThree
                orderId={active ? orderRequest?.id : undefined}
                orderUrl={active ? orderRequest?.url : undefined}
              />
            )}
          </Wizard>
        ))}
    </CartWizardContainer>
  );
};

CartWizard.defaultProps = {
  active: true
};
export default CartWizard;
